import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { API_URL, fetchWithCredentials } from '../config';

const DATASET_TYPES = [
  // Core Sales & Product Data
  { id: 'product_info', label: 'Product Info', type: 'Master Data', icon: '📦' },
  { id: 'sell_through', label: 'Sell Through', type: 'Sales Data', icon: '💰' },
  { id: 'sell_in', label: 'Sell In', type: 'Sales Data', icon: '📥' },
  { id: 'productivity', label: 'Productivity', type: 'Performance', icon: '📊' },
  
  // Web Analytics
  { id: 'ga4', label: 'GA4', type: 'Web Analytics', icon: '📈' },
  { id: 'website_analytics', label: 'Website Analytics', type: 'Web Analytics', icon: '🌐' },
  { id: 'pdp_performance', label: 'PDP Performance', type: 'Web Analytics', icon: '🛍️' },
  
  // Marketing
  { id: 'marketing_spend', label: 'Marketing Spend', type: 'Marketing', icon: '💸' },
  { id: 'paid_search', label: 'Paid Search', type: 'Marketing', icon: '🔍' },
  { id: 'organic_search', label: 'Organic Search', type: 'Marketing', icon: '🌱' },
  { id: 'email_campaigns', label: 'Email Campaigns', type: 'Marketing', icon: '📧' },
  { id: 'newsletter_sponsorships', label: 'Newsletter Sponsorships', type: 'Marketing', icon: '📰' },
  
  // External Data
  { id: 'search_trends', label: 'Search Trends', type: 'External Data', icon: '📉' },
  { id: 'competitor_pricing', label: 'Competitor Pricing', type: 'External Data', icon: '🏷️' },
  { id: 'macro_signals', label: 'Macro Signals', type: 'External Data', icon: '📊' },
  { id: 'social_buzz', label: 'Social Buzz', type: 'External Data', icon: '💬' },
  { id: 'market_performance', label: 'Market Performance', type: 'Performance', icon: '🌍' },
  
  // Reference Data
  { id: 'calendar', label: 'Calendar', type: 'Reference Data', icon: '📅' },
  { id: 'markets', label: 'Markets', type: 'Reference Data', icon: '🗺️' },
  { id: 'retailer_market_join', label: 'Retailer Market Join', type: 'Reference Data', icon: '🔗' },
  { id: 'url_product_mapping', label: 'URL to Product', type: 'Reference Data', icon: '🔗' },
  { id: 'utm_campaign_registry', label: 'UTM Campaign Registry', type: 'Reference Data', icon: '🏷️' },
  { id: 'channel_fees', label: 'Channel Fee Assumptions', type: 'Reference Data', icon: '💵' },
  { id: 'channel_margin', label: 'Channel Margin', type: 'Reference Data', icon: '💵' }
];

// Group by type for better UI
const getDatasetTypeGroups = () => {
  const groups = {};
  DATASET_TYPES.forEach(dt => {
    if (!groups[dt.type]) groups[dt.type] = [];
    groups[dt.type].push(dt);
  });
  return groups;
};

const DatasetUploadModal = ({ 
  open, 
  onClose, 
  selectedCategory, 
  onCategoryChange, 
  onUpload 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setUploadError(null);
      } else {
        setUploadError('Please select a CSV file');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCategory) return;
    
    setUploading(true);
    setUploadError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', selectedCategory);
      
      const response = await fetchWithCredentials(`${API_URL}/api/upload-dataset`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Upload successful:', result);
      
      // Reset form and close modal
      setSelectedFile(null);
      onUpload(result);
      
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUpload />
        Upload Dataset
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3, mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Dataset Type</InputLabel>
            <Select
              value={selectedCategory}
              label="Dataset Type"
              onChange={(e) => onCategoryChange(e.target.value)}
              sx={{ mb: 1 }}
            >
              {Object.entries(getDatasetTypeGroups()).map(([groupName, items]) => [
                <MenuItem disabled key={`header-${groupName}`} sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {groupName}
                </MenuItem>,
                ...items.map((dt) => (
                  <MenuItem key={dt.id} value={dt.id} sx={{ pl: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{dt.icon}</span>
                      <Typography variant="body2">{dt.label}</Typography>
                    </Box>
                  </MenuItem>
                ))
              ]).flat()}
            </Select>
          </FormControl>

          {selectedCategory && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {DATASET_TYPES.find(c => c.id === selectedCategory)?.label}
                {' - '}
                This will replace any existing {DATASET_TYPES.find(c => c.id === selectedCategory)?.label} dataset in your corpus.
              </Typography>
            </Alert>
          )}
        </Box>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          <Box
            component="label"
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
                setSelectedFile(file);
                setUploadError(null);
              } else {
                setUploadError('Please select a CSV file');
              }
            }}
            onDragOver={(e) => e.preventDefault()}
            sx={{
              border: '2px dashed',
              borderColor: selectedFile ? 'success.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: selectedFile ? 'rgba(76, 175, 80, 0.05)' : 'background.secondary',
              cursor: 'pointer',
              display: 'block',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(71, 189, 198, 0.05)'
              }
            }}
          >
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {selectedFile ? selectedFile.name : 'Drop CSV file here or click to browse'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedFile 
                ? `File size: ${(selectedFile.size / 1024).toFixed(1)} KB`
                : 'Supports .csv files up to 100MB'
              }
            </Typography>
          </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleUpload}
          disabled={!selectedCategory || !selectedFile || uploading}
        >
          {uploading ? 'Uploading...' : 'Upload Dataset'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DatasetUploadModal;
